import { Capacitor, registerPlugin } from "@capacitor/core";
import { getNativeApiOrigin } from "./nativeApiOrigin";
import { replaceBootstrapTenantPath, tenantRoute } from "../utils/tenantRoute";
import { resetTenantScope } from "../contexts/tenantScopeHeaders";
import type { PluginListenerHandle } from "@capacitor/core";

/**
 * Bridge to native Stytch B2B login (iOS Capacitor shell).
 * Native side: ios/App/App/StytchAuthPlugin.swift.
 *
 * The native SDK owns the session (Keychain, rolling 30-day idle window);
 * the web layer consumes short-lived session JWTs and sends them as
 * Authorization: Bearer via the existing embed-auth machinery.
 */
export interface NativeSession {
    sessionJwt: string;
    /** ISO-8601; absent while the SDK hasn't hydrated the member session yet. */
    expiresAt?: string;
    /** First native app launch only: selector from source-bound cached metadata. */
    resumeProjectPath?: string;
    resumeApiOrigin?: string;
}

const FALLBACK_JWT_TTL_SECONDS = 300;

/** Convert a native Stytch session into the token shape used by the SPA. */
export function nativeTokensFrom(session: NativeSession) {
    let exp = Math.floor(Date.now() / 1000) + FALLBACK_JWT_TTL_SECONDS;

    try {
        const payload = session.sessionJwt.split(".")[1];
        if (payload) {
            const base64 = payload.replace(/-/g, "+").replace(/_/g, "/");
            const normalized = base64.padEnd(Math.ceil(base64.length / 4) * 4, "=");
            const decoded = JSON.parse(window.atob(normalized)) as { exp?: unknown };
            if (typeof decoded.exp === "number" && Number.isFinite(decoded.exp)) {
                exp = decoded.exp;
            }
        }
    } catch {
        // Stytch JWTs normally contain exp; use the SDK's five-minute JWT
        // lifetime if a future token format cannot be decoded in the WebView.
    }

    return {
        sessionJwt: session.sessionJwt,
        exp,
    };
}

interface StytchAuthPlugin {
    /** Selects the Stytch project that belongs to the resolved backend. */
    configureEnvironment(options: { environment: "local" | "staging" | "prod" }): Promise<void>;
    getSession(): Promise<{ session: NativeSession | null }>;
    /** Presents the native Stytch B2B login UI; resolves when it closes. */
    login(): Promise<{ session: NativeSession | null }>;
    /** Fresh JWT + extends the session's 30-day rolling idle window. */
    refresh(): Promise<{ session: NativeSession | null }>;
    /**
     * Revokes the native Stytch session. `wipeOfflineData: true` (explicit,
     * user-initiated Sign Out) also deletes every tenant's pinned offline
     * library and SyncDatabase rows on this device — required so a
     * shared/returned iPad never carries the previous member's downloaded
     * PDFs into the next login. Silent/forced re-auth recovery
     * (`recoverFromEmbedAuthFailure`) passes `wipeOfflineData: false` so a
     * routine session hiccup revokes the session without erasing the
     * offline library, keeping the next login warm.
     */
    logout(options: { wipeOfflineData: boolean }): Promise<void>;
    addListener(
        eventName: string,
        listenerFunc: (...args: unknown[]) => void,
    ): Promise<PluginListenerHandle>;
}

const StytchAuth = registerPlugin<StytchAuthPlugin>("StytchAuth");

export const isNativeAuthAvailable = (): boolean => Capacitor.getPlatform() === "ios";

/**
 * Fires when the NATIVE pre-auth welcome screen's "Continue to sign in" is
 * tapped (AuthWelcomeView → StytchAuthPlugin.requestNativeLogin). The web
 * `NativeLogin` component — running invisibly beneath that native cover —
 * subscribes and runs its existing `startLogin()`, keeping the whole session
 * handshake on the one proven path.
 */
export function addLoginRequestedListener(cb: () => void): Promise<PluginListenerHandle> {
    return StytchAuth.addListener("loginRequested", cb);
}

export default StytchAuth;

/** Reopen the last canonical project selector before source bootstrap releases
 * queries. This grants no online access; the provider must still admit it. */
export function restoreNativeProjectRoute(session: NativeSession): boolean {
    const path = session.resumeProjectPath;
    if (
        !isNativeAuthAvailable() ||
        window.location.pathname !== "/" ||
        window.location.search ||
        window.location.hash ||
        !path ||
        !/^\/[a-z0-9][a-z0-9_-]*\/[a-z0-9][a-z0-9_-]*\/$/i.test(path) ||
        !tenantRoute(path).companySlug ||
        !tenantRoute(path).projectSlug ||
        session.resumeApiOrigin !== getNativeApiOrigin()
    )
        return false;
    resetTenantScope();
    replaceBootstrapTenantPath(path);
    return true;
}
